import React, { useMemo } from "react";
import { Text } from "@zorni/components-lib";
import { useTranslation } from "react-i18next";

import styles from "../TripView.module.css";

const valueOfPrice = (price) => Number(price?.value ?? 0);
const currencyOfPrice = (price) => price?.currency || "DH";
const normalizeId = (value) => String(value || "").trim();

const TripProgram = ({ steps, selectedTripDate }) => {
  const { t, i18n } = useTranslation();

  const stepDatesByStepId = useMemo(() => {
    const map = new Map();

    (selectedTripDate?.stepsDate || []).forEach((stepDate) => {
      const rawDate = stepDate?.date;
      if (!rawDate) return;

      (stepDate?.stepIds || []).forEach((rawStepId) => {
        const stepId = normalizeId(rawStepId);
        if (!stepId) return;

        if (!map.has(stepId)) {
          map.set(stepId, []);
        }
        map.get(stepId).push(rawDate);
      });
    });

    return map;
  }, [selectedTripDate?.stepsDate]);

  const formatStepDate = (rawDate) => {
    const date = new Date(rawDate);
    if (Number.isNaN(date.getTime())) return rawDate;

    return date.toLocaleDateString(i18n.language || "fr-FR", {
      weekday: "short",
      day: "2-digit",
      month: "short",
    });
  };

  const getStepDateDisplay = (stepId) => {
    const normalizedStepId = normalizeId(stepId);
    const dates = stepDatesByStepId.get(normalizedStepId) || [];

    if (!dates.length) return t("tripPublication.tripView.labels.notScheduled");

    const formattedDates = Array.from(new Set(dates.map((dateValue) => formatStepDate(dateValue))));
    return formattedDates.join(" | ");
  };

  const isStepScheduled = (stepId) => {
    const normalizedStepId = normalizeId(stepId);
    return stepDatesByStepId.has(normalizedStepId);
  };

  return (
    <section className={styles.programSection}>
      <Text as="h4" size="sm" weight="bold" className={styles.programHeading}>
        {t("tripPublication.tripView.sections.program")}
      </Text>

      <div className={styles.programList}>
        {(steps || []).map((step) => {
          const stepDateDisplay = getStepDateDisplay(step?.id);
          const isScheduled = isStepScheduled(step?.id);

          return (
            <article
              key={step?.id || step?.ranking?.id || step?.ranking?.title || "step"}
              className={[styles.stepCard, !isScheduled ? styles.stepCardFaded : ""].join(" ")}
            >
              <div className={styles.stepMeta}>
                <span className={styles.rankBadge}>{step?.ranking?.rank || "-"}</span>
                <Text as="p" size="xs" weight="bold" className={styles.stepDate}>
                  {stepDateDisplay}
                </Text>
                <Text as="p" size="sm" weight="bold">
                  {step?.city || "-"}
                </Text>
                <Text as="p" size="xs" color="muted">
                  {step?.country || "-"}
                </Text>
                <div className={styles.hostingBlock}>
                  <Text as="p" size="xs" weight="bold" className={styles.hostLabel}>
                    {t("tripPublication.tripView.labels.hosting")}
                  </Text>
                  <Text as="p" size="sm">
                    {step?.hosting?.name || t("tripPublication.tripView.empty.noHosting")}
                  </Text>
                  {!!step?.hosting?.stars && (
                    <Text as="p" size="xs" className={styles.hostStars}>
                      {"\u2605".repeat(step.hosting.stars)}
                    </Text>
                  )}
                </div>
              </div>

              <div className={styles.stepContent}>
                <Text as="p" size="md" weight="bold">
                  {step?.ranking?.title || "-"}
                </Text>
                <Text as="p" size="sm" color="muted">
                  {step?.description || "-"}
                </Text>

                <div className={styles.itemsGrid}>
                  <div>
                    <Text as="p" size="xs" weight="bold" className={styles.itemHeader}>
                      {t("tripPublication.tripView.sections.inclusions")}
                    </Text>
                    {(step?.activities || []).map((activity) => (
                      <div
                        className={[styles.listRow, styles.inclusionRow].join(" ")}
                        key={activity?.id || `${step?.id}-activity`}
                      >
                        <Text
                          as="span"
                          size="sm"
                          className={activity?.isIncluded ? styles.includedTitle : styles.standardTitle}
                        >
                          {activity?.title || "-"}
                        </Text>
                        <Text as="span" size="sm" weight="bold">
                          {activity?.isIncluded
                            ? t("tripPublication.tripView.labels.included")
                            : `${valueOfPrice(activity?.adultPricePerPerson)} ${currencyOfPrice(activity?.adultPricePerPerson)}`}
                        </Text>
                      </div>
                    ))}
                    {!step?.activities?.length && (
                      <Text as="p" size="sm" color="muted">
                        {t("tripPublication.tripView.empty.noActivity")}
                      </Text>
                    )}
                  </div>

                  <div>
                    <Text as="p" size="xs" weight="bold" className={styles.itemHeader}>
                      {t("tripPublication.tripView.sections.options")}
                    </Text>
                    {(step?.options || []).map((option) => (
                      <div
                        className={[styles.listRow, styles.optionRow].join(" ")}
                        key={option?.id || `${step?.id}-option`}
                      >
                        <Text as="span" size="sm" className={styles.standardTitle}>
                          {option?.title || "-"}
                        </Text>
                        <Text as="span" size="sm" weight="bold">
                          {`${valueOfPrice(option?.adultPricePerPerson)} ${currencyOfPrice(option?.adultPricePerPerson)}`}
                        </Text>
                      </div>
                    ))}
                    {!step?.options?.length && (
                      <Text as="p" size="sm" color="muted">
                        {t("tripPublication.tripView.empty.noOption")}
                      </Text>
                    )}
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
};

export default TripProgram;
